/**
 * Custom hooks per company settings (Admin)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiGet,
  apiPut,
} from '@/lib/api-client';
import type {
  CompanySettings,
  CompanySettingsUpdate,
} from '@/types/api/admin';

const COMPANY_SETTINGS_QUERY_KEYS = {
  all: ['company-settings'] as const,
  detail: (companyId: string) => [...COMPANY_SETTINGS_QUERY_KEYS.all, companyId] as const,
};

/**
 * Hook per ottenere settings di una company
 */
export function useCompanySettings(companyId: string) {
  return useQuery({
    queryKey: COMPANY_SETTINGS_QUERY_KEYS.detail(companyId),
    queryFn: async (): Promise<CompanySettings> => {
      return await apiGet<CompanySettings>(`/companies/${companyId}/settings`);
    },
    enabled: !!companyId,
  });
}

/**
 * Hook per aggiornare company settings
 * Nota: L'API backend usa query params invece di body per questo endpoint
 */
export function useUpdateCompanySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId, data }: { companyId: string; data: CompanySettingsUpdate }): Promise<CompanySettings> => {
      // L'API usa query params invece di body per l'update
      // Costruiamo l'URL con query params
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
      
      const url = `/companies/${companyId}/settings${params.toString() ? `?${params.toString()}` : ''}`;
      
      return await apiPut<CompanySettings>(url, undefined);
    },
    onSuccess: (_data, variables) => {
      // Invalida settings della company
      queryClient.invalidateQueries({ queryKey: COMPANY_SETTINGS_QUERY_KEYS.detail(variables.companyId) });
    },
  });
}

